package co.maytes.standupbot.dashboard.features.settings

import co.maytes.standupbot.shared.dto.logs.LogEventDto

enum class LogLevelFilter(val label: String, val levels: Set<String>) {
    ALL("All", setOf("INFO", "WARN", "ERROR")),
    WARN("Warn+", setOf("WARN", "ERROR")),
    ERROR("Error", setOf("ERROR")),
}

enum class LogSeverity { Info, Warn, Error }

object LogsView {
    const val REFRESH_INTERVAL_MS = 10_000L
    const val RESOURCE = "the logs"
    const val SHOW_ALL = "Show all events"
    const val LOAD_OLDER = "Load older"
    const val LOADING_OLDER = "Loading..."
    private const val MESSAGE_PREVIEW_MAX = 200
    private const val SOURCE_MAX = 32
    private const val SOURCE_SEGMENTS = 2
    private const val MILLIS_PER_SECOND = 1000L

    fun refreshNote(): String =
        "Refreshes every ${REFRESH_INTERVAL_MS / MILLIS_PER_SECOND} seconds. " +
            "Use it to trace failed GitHub fetches, Slack updates and synthesis."

    fun severity(level: String): LogSeverity = when (level.uppercase()) {
        "ERROR" -> LogSeverity.Error
        "WARN" -> LogSeverity.Warn
        else -> LogSeverity.Info
    }

    fun levelLabel(level: String): String = when (severity(level)) {
        LogSeverity.Error -> "Error"
        LogSeverity.Warn -> "Warn"
        LogSeverity.Info -> level.lowercase().replaceFirstChar { it.uppercase() }.ifBlank { "Info" }
    }

    fun summary(events: List<LogEventDto>): LogSummary {
        val errors = events.count { severity(it.level) == LogSeverity.Error }
        val warnings = events.count { severity(it.level) == LogSeverity.Warn }
        val window = if (events.size == 1) "the latest event" else "the latest ${events.size} events"
        val parts = listOfNotNull(
            errors.takeIf { it > 0 }?.let { "$it ${if (it == 1) "error" else "errors"}" },
            warnings.takeIf { it > 0 }?.let { "$it ${if (it == 1) "warning" else "warnings"}" },
        )
        return when {
            events.isEmpty() -> LogSummary("Nothing to report", ".")
            parts.isEmpty() -> LogSummary("No warnings or errors", " in $window.")
            parts.size == 1 -> LogSummary(parts[0], " in $window.")
            else -> LogSummary(parts[0], " and ${parts[1]} in $window.")
        }
    }

    fun emptyText(filter: LogLevelFilter): String = when (filter) {
        LogLevelFilter.ALL -> "No events yet."
        LogLevelFilter.WARN -> "No warnings or errors yet."
        LogLevelFilter.ERROR -> "No errors yet."
    }

    fun preview(message: String): String =
        if (message.length > MESSAGE_PREVIEW_MAX) message.take(MESSAGE_PREVIEW_MAX) + "..." else message

    fun sourceLabel(logger: String): String {
        val short = logger.split('.').takeLast(SOURCE_SEGMENTS).joinToString(".")
        return if (short.length <= SOURCE_MAX) short else "..." + short.takeLast(SOURCE_MAX - "...".length)
    }

    fun detailRows(event: LogEventDto): List<LogDetailRow> = buildList {
        add(LogDetailRow("id", event.id))
        add(LogDetailRow("time", event.createdAt.toString()))
        add(LogDetailRow("logger", event.logger))
        add(LogDetailRow("message", event.message))
        if (event.mdc.isNotEmpty()) {
            add(LogDetailRow("mdc", event.mdc.entries.sortedBy { it.key }.joinToString("\n") { (key, value) -> "$key = $value" }))
        }
    }
}
